import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import {
  DEFAULT_USER_SETTINGS,
  fetchUserSettings,
  fulfillmentFlowBetaPatch,
  patchUserSettings,
  readCachedUserSettings,
  writeCachedUserSettings,
  type UserSettings,
} from '../services/userSettingsAPI';

interface UserSettingsContextValue {
  settings: UserSettings;
  setColorMode: (enabled: boolean) => Promise<void>;
  setCameraOpensOrderDetails: (enabled: boolean) => Promise<void>;
  setCollapseLocatedBundles: (enabled: boolean) => Promise<void>;
  setPhotographPackingItems: (enabled: boolean) => Promise<void>;
  setPrintScanOrderLabel: (enabled: boolean) => Promise<void>;
  setScanSuggestBoxSize: (enabled: boolean) => Promise<void>;
  setFulfillmentFlowBeta: (enabled: boolean) => Promise<void>;
}

const UserSettingsContext = createContext<UserSettingsContextValue | null>(null);

export function useUserSettings(): UserSettingsContextValue {
  const context = useContext(UserSettingsContext);
  if (!context) {
    throw new Error('useUserSettings must be used within a UserSettingsProvider');
  }
  return context;
}

function applyColorMode(enabled: boolean): void {
  if (typeof document === 'undefined') return;
  document.body.classList.toggle('colorblind-mode', enabled);
}

interface UserSettingsProviderProps {
  readonly children: React.ReactNode;
}

export function UserSettingsProvider({ children }: UserSettingsProviderProps): React.ReactElement {
  const { user } = useAuth();
  const email = user?.email ?? '';
  const [settings, setSettings] = useState<UserSettings>(() => {
    return readCachedUserSettings(email) ?? DEFAULT_USER_SETTINGS;
  });
  const [boundEmail, setBoundEmail] = useState(email);
  if (email !== boundEmail) {
    setBoundEmail(email);
    setSettings(email ? (readCachedUserSettings(email) ?? DEFAULT_USER_SETTINGS) : DEFAULT_USER_SETTINGS);
  }

  useEffect(() => {
    applyColorMode(settings.colorMode);
  }, [settings.colorMode]);

  useEffect(() => {
    return () => {
      applyColorMode(false);
    };
  }, []);

  useEffect(() => {
    if (!email) {
      setSettings(DEFAULT_USER_SETTINGS);
      return;
    }

    const cached = readCachedUserSettings(email);
    if (cached) {
      setSettings(cached);
    }

    let cancelled = false;
    void fetchUserSettings()
      .then((next) => {
        if (cancelled) return;
        setSettings(next);
        writeCachedUserSettings(email, next);
      })
      .catch((error: unknown) => {
        console.error('Failed to load user settings:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [email]);

  const persistPatch = async (patch: Partial<UserSettings>): Promise<void> => {
    const previous = settings;
    const optimistic: UserSettings = { ...previous, ...patch };
    setSettings(optimistic);
    writeCachedUserSettings(email, optimistic);
    try {
      const saved = await patchUserSettings(patch);
      setSettings(saved);
      writeCachedUserSettings(email, saved);
    } catch (error: unknown) {
      setSettings(previous);
      writeCachedUserSettings(email, previous);
      throw error;
    }
  };

  const setColorMode = async (enabled: boolean): Promise<void> => {
    await persistPatch({ colorMode: enabled });
  };

  const setCameraOpensOrderDetails = async (enabled: boolean): Promise<void> => {
    await persistPatch({ cameraOpensOrderDetails: enabled });
  };

  const setCollapseLocatedBundles = async (enabled: boolean): Promise<void> => {
    await persistPatch({ collapseLocatedBundles: enabled });
  };

  const setPhotographPackingItems = async (enabled: boolean): Promise<void> => {
    await persistPatch({ photographPackingItems: enabled });
  };

  const setPrintScanOrderLabel = async (enabled: boolean): Promise<void> => {
    await persistPatch({ printScanOrderLabel: enabled });
  };

  const setScanSuggestBoxSize = async (enabled: boolean): Promise<void> => {
    await persistPatch({ scanSuggestBoxSize: enabled });
  };

  const setFulfillmentFlowBeta = async (enabled: boolean): Promise<void> => {
    await persistPatch(fulfillmentFlowBetaPatch(enabled));
  };

  const value: UserSettingsContextValue = {
    settings,
    setColorMode,
    setCameraOpensOrderDetails,
    setCollapseLocatedBundles,
    setPhotographPackingItems,
    setPrintScanOrderLabel,
    setScanSuggestBoxSize,
    setFulfillmentFlowBeta,
  };

  return <UserSettingsContext.Provider value={value}>{children}</UserSettingsContext.Provider>;
}
